/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/outline';
import {
  ArrowRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  PlusIcon,
  PlusSmIcon,
  XCircleIcon,
  XIcon,
} from '@heroicons/react/solid';
import Button from '../Button';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import Queue from '../Queue';
import SpinnerIcon from '../Icons/spinner';
import { useRouter } from 'next/router';

import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';

import toast, { Toaster } from 'react-hot-toast';
import { Tooltip } from 'react-tippy';
import TimeSlot from '../Scheduling/TimeSlot';

const { DateTime } = require('luxon');

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function SlotEditor({ isOpen, setOpen, userID, refreshSlots }) {
  const [selectedTime, setSelectedTime] = useState(null);
  const [day, setDay] = useState('Every Day');
  const [slotSuccess, setSlotSuccess] = useState(false);
  const [changedSlots, setChangedSlots] = useState([]);

  const defaultSlots = {
    monday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    tuesday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    wednesday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    thursday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    friday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    saturday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
    sunday: {
      slots: [
        {
          h: 8,
          m: 0,
        },
        {
          h: 12,
          m: 0,
        },
        {
          h: 17,
          m: 0,
        },
      ],
    },
  };

  const [slots, setSlots] = useState(defaultSlots);

  const removeSlot = (day, slotIndex) => {
    const newSlots = { ...slots };
    newSlots[day].slots.splice(slotIndex, 1);
    setSlots(newSlots);
  };

  const addSlot = () => {
    if (!selectedTime && !day) {
      console.log('No time or day selected');
      return;
    } else {
      const [hour, minute] = selectedTime.split(':');
      var formattedMinute = minute.replace(/^0+/, '');

      formattedMinute = formattedMinute === '' ? '0' : formattedMinute;

      //if hour is 0, set it to 12
      const formattedHour = hour === '0' ? '12' : hour;

      const newSlot = {
        h: parseInt(formattedHour),
        m: parseInt(formattedMinute),
      };

      var daysToAdd = [];
      //create a new object that is a copy of the current slots object
      const newSlots = { ...slots };

      //if day === "Every Day" then add to every day in the slots object
      if (day === 'Every Day') {
        daysToAdd = Object.keys(slots);
      } else if (day === 'Weekdays') {
        daysToAdd = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      } else if (day === 'Weekends') {
        daysToAdd = ['saturday', 'sunday'];
      } else {
        daysToAdd = [day.toLowerCase()];
      }

      daysToAdd.forEach((day) => {
        //check if the slot already exists in the day and skip if it does
        if (
          newSlots[day]?.slots.find(
            (slot) => slot.h === newSlot.h && slot.m === newSlot.m,
          )
        ) {
          return;
        } else {
          //add to the slot in the correct day
          var orderedSlots = slots[day] ? [...slots[day].slots] : [];
          //add the new slot to orderedSlots in the correct position based on the time from earliest to latest
          orderedSlots.push(newSlot);
          orderedSlots.sort((a, b) => {
            return a.h - b.h || a.m - b.m;
          });

          //create a .slots child in newSlots[day] and set it to the orderedSlots array
          newSlots[day] = {
            ...newSlots[day],
            slots: orderedSlots,
          };
        }
      });

      //get the day and index of all the new slots added
      const newSlotIndexes = daysToAdd.map((day) => {
        return {
          day: day,
          index: newSlots[day].slots.findIndex((slot) => {
            return slot.h === newSlot.h && slot.m === newSlot.m;
          }),
        };
      });

      setChangedSlots(newSlotIndexes);

      setSlots(newSlots);
      setSlotSuccess(true);

      setTimeout(() => {
        setSlotSuccess(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (changedSlots) {
      changedSlots.forEach(({ day, index }) => {
        const slot = document.getElementById(`${day}-${index}`);
        if (slot) {
          slot.classList.add('bg-blue-500/10');
          slot.classList.add('text-blue-500');

          setTimeout(() => {
            slot.classList.remove('bg-blue-500/10');
            slot.classList.remove('text-blue-500');
            setChangedSlots(null);
          }, 1000);
        }
      });
    }
  }, [slots]);

  useEffect(() => {
    if (slots !== defaultSlots) {
      //just so when it initialy sets the slots to the default slots it doesn't save and overwrite their actual slots
      //slots have changes so save them to the database
      saveSlots(slots);
    } else {
      //slots have not changed
      console.log('no slots changed');
    }
  }, [slots]);

  useEffect(() => {
    userID && readSlots(userID);
  }, [userID]);

  const saveSlots = (slots) => {
    if (userID) {
      firebase
        .database()
        .ref('users/' + userID + '/schedule')
        .child('slots')
        .update(slots)
        .then(() => {
          //use settimeout so highlight doesnt get interrupted by rerender
          refreshSlots();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
    }
  };

  const readSlots = () => {
    if (userID) {
      firebase
        .database()
        .ref('users/' + userID + '/schedule')
        .child('slots')
        .once('value', (snapshot) => {
          if (snapshot.exists()) {
            setSlots(snapshot.val());
            console.log('slots read');
            console.log(snapshot.val());
          } else {
            //setSlots(defaultSlots)
          }
        });
    }
  };

  useEffect(() => {
    slotSuccess && setSlotSuccess(false);
  }, [day, selectedTime]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-50 overflow-y-auto"
        open={isOpen}
        onClose={() => setOpen(false)}
      >
        <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-400 bg-opacity-50 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block w-full  transform  text-left align-bottom transition-all sm:my-8 sm:max-w-2xl sm:align-middle">
              <motion.div
                layout
                className="w-full overflow-hidden rounded-2xl bg-white shadow-xl"
              >
                <motion.div
                  layout={'position'}
                  className={'absolute top-2 right-2'}
                >
                  <button
                    onClick={() => setOpen(false)}
                    className="rounded-full bg-gray-100 p-2 text-gray-500 duration-200 hover:bg-gray-200 focus:outline-none"
                  >
                    <XIcon className="h-5 w-5" />
                  </button>
                </motion.div>

                <div
                  className={'flex w-full flex-col items-center justify-center'}
                >
                  <div className="w-full ">
                    <motion.div
                      layout={'position'}
                      className={
                        'flex flex-col items-start justify-start space-y-1 px-6 pt-6'
                      }
                    >
                      <Dialog.Title
                        as="h3"
                        className="text-left font-sans text-2xl font-semibold tracking-tight text-gray-800"
                      >
                        Edit Schedule
                      </Dialog.Title>
                      <p
                        className={
                          'w-full text-left font-sans text-sm text-gray-600'
                        }
                      >
                        Editing your schedule won't affect currently scheduled
                        posts. Changes are auto-saved.
                      </p>
                    </motion.div>

                    <motion.div
                      layout={'position'}
                      className={'overflow-x-scroll'}
                    >
                      <motion.div
                        layout={'position'}
                        className=" mx-6 mt-6 flex w-full min-w-[500px] flex-col sm:w-auto sm:min-w-0"
                      >
                        <motion.div
                          layout={'position'}
                          className="grid w-full grid-cols-7 items-center justify-items-center gap-2 rounded-t-lg bg-gray-100 p-1.5 align-middle text-sm font-medium"
                        >
                          <div className="col-span-1">
                            <p>Mon</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Tue</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Wed</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Thu</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Fri</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Sat</p>
                          </div>

                          <div className="col-span-1 ">
                            <p>Sun</p>
                          </div>
                        </motion.div>

                        <motion.div
                          layout={'position'}
                          className="grid w-full grid-cols-7 items-stretch justify-items-center divide-x divide-gray-100 rounded-b-lg border-b border-r border-l border-gray-100 bg-white text-sm text-gray-600"
                        >
                          <div
                            id={'monday'}
                            className="flex w-full grid-cols-1 flex-col space-y-1 p-1.5"
                          >
                            {slots.monday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'monday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'tuesday'}
                            className="flex w-full grid-cols-1 flex-col space-y-1 p-1.5"
                          >
                            {slots.tuesday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'tuesday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'wednesday'}
                            className="flex w-full grid-cols-1 flex-col items-center justify-items-center space-y-1 p-1.5"
                          >
                            {slots.wednesday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'wednesday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'thursday'}
                            className="flex w-full grid-cols-1 flex-col items-center justify-items-center space-y-1 p-1.5"
                          >
                            {slots.thursday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'thursday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'friday'}
                            className="flex w-full grid-cols-1 flex-col items-center justify-items-center space-y-1 p-1.5"
                          >
                            {slots.friday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'friday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'saturday'}
                            className="flex w-full grid-cols-1 flex-col items-center justify-items-center space-y-1 p-1.5"
                          >
                            {slots.saturday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'saturday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>

                          <div
                            id={'sunday'}
                            className="flex w-full grid-cols-1 flex-col items-center justify-items-center space-y-1 p-1.5"
                          >
                            {slots.sunday?.slots.map((slot, index) => (
                              <TimeSlot
                                day={'sunday'}
                                slotIndex={index}
                                slot={slot}
                                removeSlot={(d, ind) => removeSlot(d, ind)}
                              />
                            ))}
                          </div>
                        </motion.div>
                      </motion.div>
                    </motion.div>

                    <motion.div
                      layout={'position'}
                      className="mt-6 flex w-full flex-col gap-2 border-t border-gray-100 bg-gray-50 p-6 sm:flex-row"
                    >
                      <div className="flex w-full flex-row items-center justify-center space-x-2">
                        <select
                          id="day"
                          name="day"
                          className="block w-full rounded-md border-gray-100 py-1.5 pl-2 pr-10 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                          defaultValue="Every Day"
                          onChange={(e) => setDay(e.target.value)}
                          value={day}
                        >
                          <option>Every Day</option>
                          <option>Weekdays</option>
                          <option>Weekends</option>
                          <option>Monday</option>
                          <option>Tuesday</option>
                          <option>Wednesday</option>
                          <option>Thursday</option>
                          <option>Friday</option>
                          <option>Saturday</option>
                          <option>Sunday</option>
                        </select>
                        <p className="text-xs font-medium uppercase text-gray-500">
                          At
                        </p>

                        <input
                          onChange={(event) =>
                            setSelectedTime(event.target.value)
                          }
                          value={selectedTime}
                          className="flex w-full items-center justify-start rounded-md border-gray-100 py-1.5 pl-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                          type="time"
                          id="appt"
                          name="appt"
                          min="09:00"
                          max="18:00"
                          required
                        ></input>
                      </div>

                      <div className="w-full sm:w-auto">
                        {slotSuccess ? (
                          <Button
                            width={'full'}
                            color={'bg-green-500'}
                            size={'mini'}
                            prefix={
                              <CheckCircleIcon className="-mr-2 h-4 w-4" />
                            }
                          >
                            Slot Added
                          </Button>
                        ) : (
                          <Button
                            width={'full'}
                            disabled={!selectedTime || !day}
                            click={() => addSlot()}
                            variant={'secondary'}
                            size={'mini'}
                            prefix={<PlusIcon className="-mr-2 h-4 w-4" />}
                          >
                            Add Slot
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
